import { cn } from "@/utils/cn";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AppIcon from "../../components/Core/AppIcon/AppIcon";
import AppRangePicker, { DateRange } from "../../components/Core/AppRangePicker/AppRangePicker";
import Button from "../../components/Core/Button/Button";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import Modal from "../../components/Core/Modal/Modal";
import TableSearch from "../../components/Core/TableSearch/TableSearch";
import VirtualizedTableLayout, {
	VirtualizedTableLayoutRef,
} from "../../components/Layout/VirtualizedTableLayout";
import MultipleSelectPopover from "../../components/MultipleSelectPopover";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import { TimeReportsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { AbsenceType } from "../../configs/constants/AbsencesType";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { SPLIT_VIEW_TYPE } from "../../configs/constants/SplitViewType";
import { TIME_REPORT_STATUS } from "../../configs/constants/TimeReportStatus";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { TIME_REPORT_STATUS_KEY_VALUES } from "../../configs/timeReportStatus.config";
import useQuery from "../../hooks/useQuery";
import useQueryState, { getQueryStringValue, setQueryStringValue } from "../../hooks/useQueryState";
import useShiftKey from "../../hooks/useShift";
import { DateOrString, JobShift } from "../../models/Job";
import { SORT_ORDER, TIME_REPORT_SORTING_COLUMN } from "../../models/Sorting";
import TimeReport, { InvoiceOverride, Reported, Suggestion } from "../../models/TimeReport";
import {
	usePublishTimeReports,
	useTimeReports,
	useUpdateTimeReport,
	useBulkUpdateTimeReports,
} from "../../networking/time-report.hooks";
import { formatDate, isFuture } from "../../utils/date.time.utils";
import { toHoursAndMinutes } from "../../utils/string.utils";
import {
	convertDecimalToTime,
	filterAndSortTimeReports,
	getTimeReportStatusCounts,
} from "../../utils/timeReport.utils";
import { toast } from "../../utils/toast";
import { useSplitViewStore } from "../../stores/splitView.store";

const TimeReportsPage: React.FC = () => {
	const splitViewStore = useSplitViewStore();
	const { mutateAsync: updateTimeReportMutation } = useUpdateTimeReport();
	const { mutateAsync: bulkUpdateTimeReportsMutation } = useBulkUpdateTimeReports();
	const { mutateAsync: publishTimeReportsMutation } = usePublishTimeReports();
	const [selectedRows, setSelectedRows] = useState(new Map<string, TimeReport>());
	const [isShiftDown, setShiftDown] = useState(false);
	const [showBulkAttestModal, setShowBulkAttestModal] = useState(false);
	const [showBulkPublishModal, setShowBulkPublishModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const tableRef = useRef<VirtualizedTableLayoutRef>(null);
	const searchRef = useRef<any>(null);

	const [selectedConsultant, setSelectedConsultant] = useState<Suggestion | null>(() => {
		const queryVal = getQueryStringValue("consultant");
		if (queryVal) {
			return JSON.parse(queryVal);
		}
		return null;
	});
	const [selectedCustomerAccount, setSelectedCustomer] = useState<Suggestion | null>(() => {
		const queryVal = getQueryStringValue("customer");
		if (queryVal) {
			return JSON.parse(queryVal);
		}
		return null;
	});
	const [selectedJob, setSelectedJob] = useState<Suggestion | null>(() => {
		const queryVal = getQueryStringValue("job");
		if (queryVal) {
			return JSON.parse(queryVal);
		}
		return null;
	});
	const [selectedStatusList, setSelectedStatusList] = useState<string[]>(() => {
		const queryVal = getQueryStringValue("status_list");
		if (queryVal) {
			return JSON.parse(queryVal);
		}
		return TIME_REPORT_STATUS_KEY_VALUES.map((status) => status.key as string);
	});

	const [order, setOrder] = useQueryState<SORT_ORDER>("orderBy");
	const [sort, setSort] = useQueryState<keyof TimeReport>("sortBy");
	const [salaryBatch, setSalaryBatch] = useQueryState<string>("salaryBatch");
	const [invoiceBatch, setInvoiceBatch] = useQueryState<string>("invoiceBatch");

	const { t } = useTranslation();
	// const navigate = useNavigate();
	const query = useQuery();

	const highlight = query.get("highlight") || undefined;

	useShiftKey((down) => setShiftDown(down));

	const { showSplitView } = splitViewStore;

	const [selectedDateRange, setSelectedDateRange] = useState<
		{ startDate: Date; endDate: Date } | undefined
	>(() => {
		const rangeFromQuery = getStartEndFromQuery(query);
		if (rangeFromQuery) {
			return rangeFromQuery;
		}
		// A ?job= deep link filters on the job alone — a date range would hide
		// reports outside it, so leave the range unset (job mode requires this).
		if (query.get("job")) {
			return undefined;
		}
		// Default to current week if no query params
		return {
			startDate: moment().startOf("isoWeek").toDate(),
			endDate: moment().endOf("isoWeek").toDate(),
		};
	});

	const [pool, setPool] = useQueryState<string>("pool", { initialValue: "" });

	// Derive the backend query from the selected range (or the ?job= filter).
	// Status filtering stays client-side (see useTimeReports docs).
	const filterByJob = !selectedDateRange ? query.get("job") || undefined : undefined;
	const listQuery = selectedDateRange
		? {
				startDate: moment(selectedDateRange.startDate).format(DATE_TIME_FORMAT_TYPES.DATE),
				endDate: moment(selectedDateRange.endDate).format(DATE_TIME_FORMAT_TYPES.DATE),
		  }
		: filterByJob
		? { job: JSON.parse(filterByJob).key as string }
		: undefined;

	const { data: timeReportsData, isLoading: isLoadingTimeReports } = useTimeReports(
		{ ...listQuery, expand: "consultant,shift,job.customer,job" },
		{ enabled: !!listQuery }
	);
	const allTimeReports = timeReportsData?.timeReports ?? [];

	// Keep the shareable URL in sync with the active filter (unchanged behavior).
	useEffect(() => {
		if (selectedDateRange) {
			setQueryStringValue(
				"start",
				moment(selectedDateRange.startDate).format(DATE_TIME_FORMAT_TYPES.DATE)
			);
			setQueryStringValue(
				"end",
				moment(selectedDateRange.endDate).format(DATE_TIME_FORMAT_TYPES.DATE)
			);
		} else if (filterByJob) {
			// NOTE: Currently there is no way to both filter on job and dates
			setQueryStringValue("job", filterByJob);
			setQueryStringValue("highlight", query.get("highlight") as string);
		}

		// eslint-disable-next-line
	}, [selectedDateRange, history /*, query*/]); //  if adding query as dep it looooops

	const selectedRowsValidForAttest = useMemo(() => {
		return Array.from(selectedRows.values()).filter((tr) =>
			[TIME_REPORT_STATUS.WORKED].includes(tr.status)
		);
	}, [selectedRows]);

	const selectedRowsValidForPublish = useMemo(() => {
		return Array.from(selectedRows.values()).filter(
			(tr) => tr.status === TIME_REPORT_STATUS.ATTESTED_BY_ADMIN && !tr.isPublishedToCustomer
		);
	}, [selectedRows]);

	const formatTime = (
		shift: { start: DateOrString; end: DateOrString },
		override: { start: DateOrString; end: DateOrString }
	) => {
		const { start, end } = shift;
		const { start: overrideStart, end: overrideEnd } = override;

		let startTimeText;
		let endTimeText;

		const mStart = moment(start);
		const mEnd = moment(end);

		if (start === overrideStart) {
			startTimeText = <span>{mStart.format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}</span>;
		} else {
			startTimeText = (
				<span>
					<span className="line-through text-gray-500 mr-0.5">
						{mStart.format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}
					</span>
					{moment(overrideStart).format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}
				</span>
			);
		}

		if (end === overrideEnd) {
			endTimeText = <span>{mEnd.format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}</span>;
		} else {
			endTimeText = (
				<span>
					<span className="line-through text-gray-500 mr-0.5">
						{mEnd.format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}
					</span>
					{moment(overrideEnd).format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES)}
				</span>
			);
		}

		return (
			<div className="flex gap-2">
				{startTimeText}
				<div>-</div>
				{endTimeText}
			</div>
		);
	};

	const formatDiffColor = (billedDiff?: number): string => {
		if (billedDiff) {
			if (billedDiff > 0) {
				return "text-black";
			} else if (billedDiff < 0) {
				return "text-danger";
			}
		}
		return "text-[#888]";
	};

	const formatOrderStatus = (
		timeReportStatus: TIME_REPORT_STATUS,
		startDate: string | Date,
		absenceType?: AbsenceType,
		timeReport?: TimeReport
	) => {
		if (absenceType) {
			return (
				<span className="text-danger text-[0.9375rem]">
					{t(("timeReports.listStatus." + absenceType) as any)}
				</span>
			);
		}

		if (isFuture(startDate)) {
			return <span className="text-[#888] text-[0.9375rem]">{t("FUTURE_TIME_REPORT")}</span>;
		}

		if (
			timeReport &&
			timeReport?.invoiceBatch &&
			timeReportStatus === TIME_REPORT_STATUS.SILENT_ATTESTED
		) {
			return <span className="text-[#888] text-[0.9375rem]">{t("INVOICED")}</span>;
		}

		switch (timeReportStatus) {
			case TIME_REPORT_STATUS.ATTESTED_BY_ADMIN:
				return <span className="text-[#888] text-[0.9375rem]">{t("ATTESTED_INTERNALLY")}</span>;
			case TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER:
				return <span className="text-green-500 text-[0.9375rem]">{t("APPROVED")}</span>;
			case TIME_REPORT_STATUS.SILENT_ATTESTED:
				return <span className="text-green-500 text-[0.9375rem]">{t("SILENT_ATTESTED")}</span>;
			case TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER:
				return <span className="text-danger text-[0.9375rem]">{t("DECLINED")}</span>;
			case TIME_REPORT_STATUS.INVOICED:
				return <span className="text-[#888] text-[0.9375rem]">{t("INVOICED")}</span>;
			case TIME_REPORT_STATUS.NOT_WORKED:
				return <span className="text-danger text-[0.9375rem]">{t("NOT_WORKED")}</span>;
			case TIME_REPORT_STATUS.PENDING:
				return <span className="text-[#888] text-[0.9375rem]">{t("PENDING_REPORT")}</span>;
			case TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN:
				return <span className="text-green-500 text-[0.9375rem]">{t("APPROVED")}</span>;
			case TIME_REPORT_STATUS.WORKED:
				return <span className="text-[#888] text-[0.9375rem]">{t("REPORTED")}</span>;
		}
	};

	const toggleTimeReportDetailsSideBar = (timeReport: TimeReport) => {
		// Note: Shift + click is same as selecting the row, not toggling sidebar
		if (isShiftDown) {
			toggleSelectedRow(timeReport);
		} else {
			splitViewStore.setActiveSplitView(SPLIT_VIEW_TYPE.TIME_REPORT_DETAILS);
			if (!showSplitView) splitViewStore.toggleSplitView();
			// The sidebar fetches the report itself (React Query)
			splitViewStore.setActiveTimeReportId(timeReport.id);
		}
	};

	// Filtered time reports - all filtering including search happens client-side
	const timeReports = filterAndSortTimeReports(allTimeReports, {
		start: selectedDateRange?.startDate,
		end: selectedDateRange?.endDate,
		consultant: selectedConsultant?.key,
		customer: selectedCustomerAccount?.key,
		job: selectedJob?.key,
		statusList: selectedStatusList,
		sort: sort,
		order: order,
		pool,
		salaryBatch,
		invoiceBatch,
		searchTerm: searchTerm || undefined,
	});

	// Scroll the ?highlight= row into view once data has loaded. Only once,
	// so the user's own scrolling/filtering isn't hijacked afterwards.
	const hasScrolledToHighlight = useRef(false);
	useEffect(() => {
		if (!highlight || hasScrolledToHighlight.current || isLoadingTimeReports) return;

		const index = timeReports.findIndex((timeReport) => timeReport.id === highlight);
		if (index >= 0) {
			hasScrolledToHighlight.current = true;
			tableRef.current?.scrollToIndex(index, { align: "center" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [highlight, isLoadingTimeReports, timeReports.length]);

	// Status counts for the popover - respects all active filters including search
	const statusCounts = getTimeReportStatusCounts(allTimeReports, {
		start: selectedDateRange?.startDate,
		end: selectedDateRange?.endDate,
		consultant: selectedConsultant?.key,
		customer: selectedCustomerAccount?.key,
		job: selectedJob?.key,
		pool,
		salaryBatch,
		invoiceBatch,
		searchTerm: searchTerm || undefined,
		statusOptions: TIME_REPORT_STATUS_KEY_VALUES.map((s) => s.key as string),
	});

	function renderShiftTime(
		status: TIME_REPORT_STATUS,
		shift: JobShift,
		reported: Reported,
		invoiceOverride?: InvoiceOverride
	) {
		if (status === TIME_REPORT_STATUS.PENDING) {
			return formatTime(shift, reported);
		} else if (!invoiceOverride && shift.start === reported.start && shift.end === reported.end) {
			return formatTime(shift, reported);
		} else {
			return (
				<>
					{formatTime(shift, reported)}
					{invoiceOverride && formatTime(reported, invoiceOverride)}
				</>
			);
		}
	}

	function toggleSelectedRow(timeReport: TimeReport) {
		if (selectedRows.has(timeReport.id)) {
			selectedRows.delete(timeReport.id);
		} else {
			selectedRows.set(timeReport.id, timeReport);
		}
		setSelectedRows(new Map(selectedRows));
	}

	async function submitBulkAttest() {
		setShowBulkAttestModal(false);
		const toastId = toast.loading(
			t("timeReports.bulkAttest.loading", { count: selectedRowsValidForAttest.length })
		);
		try {
			const { updatedCount, failedCount, results } = await bulkUpdateTimeReportsMutation({
				timeReportIds: selectedRowsValidForAttest.map((tr) => tr.id),
				data: { status: TIME_REPORT_STATUS.ATTESTED_BY_ADMIN },
			});

			if (failedCount === 0) {
				toast.success(t("timeReports.bulkAttest.success"), { id: toastId });
				return;
			}

			// Name the rows that were left untouched so the user can find them in the list
			const failed = results
				.filter((row) => !row.ok)
				.map((row) => {
					const tr = selectedRows.get(row.id);
					if (!tr) return row.id;
					const name = [tr.consultant?.firstName, tr.consultant?.lastName].filter(Boolean).join(" ");
					return `${name} ${moment(tr.reported.start).format("YYYY-MM-DD")}`.trim();
				});

			toast.warning(
				t("timeReports.bulkAttest.partial", { updated: updatedCount, failed: failedCount }),
				{ id: toastId, description: failed.join(", ") }
			);
		} catch {
			toast.error(t("timeReports.bulkAttest.error"), { id: toastId });
		}
	}

	async function submitBulkPublish() {
		setShowBulkPublishModal(false);

		const toastId = toast.loading(
			t("timeReports.bulkPublish.loading", { count: selectedRowsValidForPublish.length })
		);
		await publishTimeReportsMutation(selectedRowsValidForPublish.map((tr) => tr.id)).catch(
			() => undefined
		);
		toast.success(t("timeReports.bulkPublish.success"), { id: toastId });
	}

	function selectAll() {
		for (const tr of timeReports) {
			selectedRows.set(tr.id, tr);
		}

		setSelectedRows(new Map(selectedRows));
	}

	function getDiffBillString(diff?: number) {
		if (!diff) {
			return "0 min";
		}

		return (diff < 0 ? "-" : "") + toHoursAndMinutes(Math.abs(diff));
	}

	async function updateFlag(id: string, newFlagStatus: boolean) {
		const status = await updateTimeReportMutation({
			timeReportId: id,
			update: { flag: newFlagStatus },
		})
			.then(() => true)
			.catch(() => false);
		if (status) {
			toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
		} else {
			toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	}

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};

	const handleSortColumn = async (sort?: keyof TimeReport) => {
		handleSortingOrder();
		setSort(sort);
	};

	// Handle search functionality - just update the search term state
	const handleSearch = useCallback((term: string, matchIndex: number) => {
		setSearchTerm(term || "");
	}, []);

	const handleClearSearch = useCallback(() => {
		setSearchTerm("");
	}, []);

	// Listen for Ctrl/Cmd+F to focus search
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "f") {
				e.preventDefault();
				searchRef.current?.focus();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	// Column widths configuration (must match header and body)
	// Using percentages for responsive layout that adapts to screen width
	const columnWidths = {
		checkbox: "40px",
		consultant: "15%",
		date: "12%",
		time: "195px",
		diff: "13%",
		break: "13%",
		job: "22%",
		status: "15%",
		flag: "5%",
	};

	// Memoized table header renderer
	const renderTableHeader = useCallback(() => {
		const baseCellClass = "pt-3 pb-2 pr-3 pl-3 cursor-pointer";

		return (
			<thead className="border-b-2 border-[#DBDBDB]">
				<tr className="text-left bg-[#F1F1F1] text-sm font-medium whitespace-nowrap h-3">
					<th className={cn(baseCellClass, "pl-5")} style={{ width: columnWidths.checkbox }}>
						<Checkbox
							onChange={() => {
								selectedRows.size === timeReports.length ? setSelectedRows(new Map()) : selectAll();
							}}
							isChecked={selectedRows.size === timeReports.length ? true : false}
						/>
					</th>
					<th
						className={baseCellClass}
						style={{ width: columnWidths.consultant }}
						onClick={() =>
							handleSortColumn(TIME_REPORT_SORTING_COLUMN.CONSULTANT as keyof TimeReport)
						}
					>
						<div className="flex gap-1">
							<div>{t("CONSULTANT")}</div>
							<SortIcon
								sortType={
									sort === (TIME_REPORT_SORTING_COLUMN.CONSULTANT as keyof TimeReport)
										? order
										: SORT_ORDER.DEFAULT
								}
							/>
						</div>
					</th>
					<th className={baseCellClass} style={{ width: columnWidths.date }}>
						{t("DATE")}
					</th>
					<th
						className={baseCellClass}
						style={{ width: columnWidths.time }}
						onClick={() => handleSortColumn(TIME_REPORT_SORTING_COLUMN.DURATION)}
					>
						<div className="flex gap-1">
							<div>{t("TIME")}</div>
							<SortIcon
								sortType={sort === TIME_REPORT_SORTING_COLUMN.DURATION ? order : SORT_ORDER.DEFAULT}
							/>
						</div>
					</th>
					<th
						className={baseCellClass}
						style={{ width: columnWidths.diff }}
						onClick={() => handleSortColumn(TIME_REPORT_SORTING_COLUMN.DIFF_BILLED_TIME)}
					>
						<div className="flex gap-1">
							<div>{t("DIFF_BILLED_TIME")}</div>
							<SortIcon
								sortType={
									sort === TIME_REPORT_SORTING_COLUMN.DIFF_BILLED_TIME ? order : SORT_ORDER.DEFAULT
								}
							/>
						</div>
					</th>
					<th className={baseCellClass} style={{ width: columnWidths.break }}>
						{t("BREAK_AND_DEDUCTION")}
					</th>
					<th
						className={baseCellClass}
						style={{ width: columnWidths.job }}
						onClick={() => handleSortColumn(TIME_REPORT_SORTING_COLUMN.ORDER as keyof TimeReport)}
					>
						<div className="flex gap-1">
							<div>{t("JOB")}</div>
							<SortIcon
								sortType={
									sort === (TIME_REPORT_SORTING_COLUMN.ORDER as keyof TimeReport)
										? order
										: SORT_ORDER.DEFAULT
								}
							/>
						</div>
					</th>
					<th
						className={baseCellClass}
						style={{ width: columnWidths.status }}
						onClick={() => handleSortColumn(TIME_REPORT_SORTING_COLUMN.STATUS)}
					>
						{t("STATUS")}{" "}
						<SortIcon
							sortType={sort === TIME_REPORT_SORTING_COLUMN.STATUS ? order : SORT_ORDER.DEFAULT}
						/>
					</th>
					<th className={baseCellClass} style={{ width: columnWidths.flag }}>
						&nbsp;
					</th>
				</tr>
			</thead>
		);
	}, [selectedRows.size, timeReports.length, sort, order, t]);

	// Memoized row renderer
	const renderTableRow = useCallback(
		(index: number) => {
			const timeReport = timeReports[index];
			const isDeclined = timeReport.status === TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER;
			const isHighlighted = highlight === timeReport.id;

			// Show yellow highlighting when there's an active search
			// All visible rows match the search (filtering happens in store)
			const isSearchMatch = searchTerm.length > 0;

			const cellBgColor = isDeclined
				? "#FFEBEB"
				: isHighlighted
				? "#bdecd1"
				: isSearchMatch
				? "#FFF4CC"
				: undefined;

			// Shared cell classes
			const baseCellClass = "p-3 py-2 border-t border-[#e7e7e7] border-solid align-middle";
			const clickableCellClass = `${baseCellClass} select-none`;

			// Style for first cell
			const firstCellStyle = { backgroundColor: cellBgColor, width: columnWidths.checkbox };

			// Style for other cells
			const cellStyle = { backgroundColor: cellBgColor };

			return (
				<>
					<td className={cn(baseCellClass, "pl-5")} style={firstCellStyle}>
						<Checkbox
							onChange={() => toggleSelectedRow(timeReport)}
							isChecked={selectedRows.has(timeReport.id)}
						/>
					</td>
					<td
						className={clickableCellClass}
						data-contact={timeReport.consultant?.id}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.consultant }}
					>
						{timeReport.consultant?.firstName || ""} {timeReport.consultant?.lastName || ""}
					</td>
					<td
						className={clickableCellClass}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.date }}
					>
						{formatDate(timeReport.shift?.start)}
					</td>
					<td
						className={cn(clickableCellClass)}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.time }}
					>
						{renderShiftTime(
							timeReport.status,
							timeReport.shift,
							timeReport.reported,
							timeReport.invoiceOverride
						)}
					</td>
					<td
						className={clickableCellClass}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.diff }}
					>
						<span className={formatDiffColor(timeReport.billedDiff)}>
							{getDiffBillString(timeReport.billedDiff)}
						</span>
					</td>
					<td
						className={clickableCellClass}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.break }}
					>
						<>
							{timeReport.invoiceOverride?.break
								? convertDecimalToTime(timeReport.invoiceOverride?.break)
								: timeReport.reported.break
								? convertDecimalToTime(timeReport.reported.break)
								: "-"}
							<br />
							{timeReport.reported.deduction ? `${timeReport.reported.deduction} kr` : "0 kr"}
						</>
					</td>
					<td
						className={clickableCellClass}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.job }}
					>
						<div className="text-sm">#{timeReport.job?.orderNumber}</div>
						{timeReport.job?.customer?.name}
					</td>
					<td
						className={clickableCellClass}
						onClick={() => toggleTimeReportDetailsSideBar(timeReport)}
						style={{ ...cellStyle, width: columnWidths.status }}
					>
						<div className="flex items-center">
							<div>
								{formatOrderStatus(
									timeReport.status,
									timeReport.shift.start,
									timeReport.absence?.type,
									timeReport
								)}
							</div>
							{timeReport.isPublishedToCustomer && (
								<div className="ml-[0.7rem]">
									<AppIcon icon="mail" size="14px" />
								</div>
							)}
						</div>
					</td>
					<td
						className={clickableCellClass}
						onClick={() => {
							updateFlag(timeReport.id, !timeReport?.flag);
						}}
						style={{ ...cellStyle, width: columnWidths.flag }}
					>
						<AppIcon icon={timeReport.flag ? "black-flag" : "white-flag"} size="15px" />
					</td>
				</>
			);
		},
		[timeReports, selectedRows, highlight, searchTerm]
	);

	return (
		<>
			<Breadcrumbs
				items={TimeReportsBreadcrumbs}
				selectedPool={pool}
				onChangePool={setPool}
				showPoolDropdown
			/>
			<div className="h-[40px] px-4 mt-3">
				<div className="flex w-full justify-between gap-3">
					<div className="flex gap-3">
						<div className="w-fit">
							<AppRangePicker
								placeholder={t("timeReports.selectDateRange")}
								containerMargin="0"
								margin="0"
								value={
									selectedDateRange
										? {
												start: selectedDateRange.startDate,
												end: selectedDateRange.endDate,
										  }
										: undefined
								}
								onChange={(range?: DateRange) =>
									range &&
									setSelectedDateRange({
										startDate: range.start,
										endDate: range.end,
									})
								}
							/>
						</div>
						<div className="bg-white flex items-center max-w-[200px] border border-[#dbdbdb] h-[34px] rounded-[2px] p-2">
							<MultipleSelectPopover
								label={t("STATUS")}
								className="full-width"
								popoverWidth={200}
								selectedValues={selectedStatusList}
								options={TIME_REPORT_STATUS_KEY_VALUES}
								counts={statusCounts}
								onSelect={(val) => {
									if (selectedStatusList.includes(val as string)) {
										setSelectedStatusList((prev) => {
											const newList: string[] = prev.filter((s) => s !== val);
											// If all statuses are selected, remove the query param entirely (undefined)
											// Otherwise, set the query param with the selected statuses
											setQueryStringValue(
												"status_list",
												newList.length === TIME_REPORT_STATUS_KEY_VALUES.length
													? undefined
													: JSON.stringify(newList)
											);
											return newList;
										});
									} else {
										setSelectedStatusList((prev) => {
											const newList: string[] = [...prev, val as string];
											// If all statuses are selected, remove the query param entirely (undefined)
											// Otherwise, set the query param with the selected statuses
											setQueryStringValue(
												"status_list",
												newList.length === TIME_REPORT_STATUS_KEY_VALUES.length
													? undefined
													: JSON.stringify(newList)
											);
											return newList;
										});
									}
								}}
								popoverPosition="left"
							/>
						</div>
					</div>
					<div className="min-w-[450px]">
						<TableSearch
							ref={searchRef}
							onSearch={handleSearch}
							matchCount={searchTerm ? timeReports.length : 0}
							placeholder="Sök/filtrera tidrapporter"
						/>
					</div>
				</div>
			</div>
			<div className="[&_tbody_td]:select-none [&_.full-width]:w-full">
				{/* Floating selection toolbar */}
				{selectedRows.size > 0 && (
					<div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 bg-white border border-[#DBDBDB] rounded-lg shadow-lg flex items-center gap-4">
						<span className="font-medium">
							{t("timeReports.selectedRows", { count: selectedRows.size })}:
						</span>
						<a
							className="text-action underline cursor-pointer hover:text-[#1a7da8] transition-colors"
							onClick={() => setShowBulkAttestModal(true)}
						>
							{t("timeReports.bulkAttest.attest")}{" "}
							{selectedRowsValidForAttest.length > 0 && `(${selectedRowsValidForAttest.length})`}
						</a>
						<a
							className="text-action underline cursor-pointer hover:text-[#1a7da8] transition-colors"
							onClick={() => setShowBulkPublishModal(true)}
						>
							{t("timeReports.bulkPublish.publish")}{" "}
							{selectedRowsValidForPublish.length > 0 && `(${selectedRowsValidForPublish.length})`}
						</a>
					</div>
				)}

				{isLoadingTimeReports ? (
					<LoadingIndicator className="mt-4" />
				) : (
					<>
						{timeReports.length > 0 ? (
							<VirtualizedTableLayout
								ref={tableRef}
								itemCount={timeReports.length}
								estimatedRowHeight={60}
								overscan={5}
								renderHeader={renderTableHeader}
								renderRow={renderTableRow}
								containerHeight="calc(100vh - 100px)"
								selectable={true}
							/>
						) : (
							<EmptyState margin="2rem 0" label="NO_TIME_REPORTS" />
						)}
					</>
				)}
				{showBulkAttestModal && (
					<Modal centerContent={false} onClose={() => setShowBulkAttestModal(false)}>
						<p className="font-medium mb-4">
							{t("timeReports.bulkAttest.info", {
								count: selectedRowsValidForAttest.length,
							})}
						</p>
						{selectedRowsValidForAttest.length !== selectedRows.size && (
							<p className="mb-4 text-danger">{t("timeReports.bulkAttest.warning")}</p>
						)}

						<Button margin="0 0 0.5rem 0" onClick={submitBulkAttest}>
							{t("timeReports.bulkAttest.attest")}
						</Button>
						<Button backgroundColor="secondary" onClick={() => setShowBulkAttestModal(false)}>
							{t("CANCEL")}
						</Button>
					</Modal>
				)}
				{showBulkPublishModal && (
					<Modal centerContent={false} onClose={() => setShowBulkPublishModal(false)}>
						<p className="font-medium mb-4">
							{t("timeReports.bulkPublish.info", {
								count: selectedRowsValidForPublish.length,
							})}
						</p>
						{selectedRowsValidForPublish.length !== selectedRows.size && (
							<p className="mb-4 text-danger">{t("timeReports.bulkPublish.warning")}</p>
						)}

						<Button margin="0 0 0.5rem 0" onClick={submitBulkPublish}>
							{t("timeReports.bulkPublish.publish")}
						</Button>
						<Button backgroundColor="secondary" onClick={() => setShowBulkPublishModal(false)}>
							{t("CANCEL")}
						</Button>
					</Modal>
				)}
			</div>
		</>
	);
};

export default TimeReportsPage;

function getStartEndFromQuery(query: URLSearchParams) {
	if (query.has("start") && query.has("end")) {
		return {
			startDate: moment(query.get("start")).toDate(),
			endDate: moment(query.get("end")).toDate(),
		};
	} else if (query.has("period")) {
		if (query.get("period") === "week") {
			return {
				startDate: moment(moment().startOf("isoWeek")).toDate(),
				endDate: moment(moment().endOf("isoWeek")).toDate(),
			};
		} else if (query.get("period") === "month") {
			return {
				startDate: moment(moment().startOf("month")).toDate(),
				endDate: moment(moment().endOf("month")).toDate(),
			};
		}
	}
	return undefined;
}
